import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { concat, flatMap, isEmpty, map } from 'lodash';
import Axios from 'src/services/axios';
import { toast } from 'react-toastify';
import type { AppThunkDispatch } from 'src/store.types';
import { ViewDataState } from 'src/types/Domain';
import { clearAndCloseRightContainer } from 'src/components/RightContainer/RightContainer.slice';
import { provideAppName } from 'src/utils/Domain/Perspective';
import ServiceContainer from 'src/ServiceContainer';
import {
  PlanTrackerSlice,
  PlanItemStatus,
  PlanItem,
  UndoItem,
  AddToAssortmentTracker,
  AddToAssortmentStatus,
} from './Planning.types';

export const UNDO_ENDPOINT = '/api/property/undo';

const initPlanTrackerState: PlanTrackerSlice = {
  shouldCheckAssortment: true,
  askForRefresh: false,
  assortmentPlanInProgress: false,
  planItems: [],
  allocPlanItems: [],
  undoViewState: ViewDataState.idle,
  undoItems: [],
  addToAsstInProg: false,
  addToAsstItems: [],
  assortmentPlanError: false,
  allocationPlanError: false,
  addToAsstError: false,
};

const planTrackerSlice = createSlice({
  name: 'PlanTracker',
  initialState: initPlanTrackerState,
  reducers: {
    init: () => initPlanTrackerState,
    startPlan: (state) => {
      const pending = state.planItems.filter((it) => it.status === PlanItemStatus.Pending) || [];
      const failed = state.planItems.filter((it) => it.status === PlanItemStatus.Failed) || [];
      if (pending.length > 0 || failed.length > 0) {
        state.assortmentPlanInProgress = true;
        state.shouldCheckAssortment = false;
        state.askForRefresh = false;
      }
    },
    planItemUpdate: (
      state,
      action: PayloadAction<{ items: Record<PlanItemStatus, PlanItem[]>; allocationPlanQueueUpdate: boolean }>,
    ) => {
      const { pending, queued, processing, predicting, completed, failed } = action.payload.items;
      if (state.assortmentPlanInProgress == true && processing.length == 0 && isEmpty(pending)) {
        state.assortmentPlanInProgress = false;
        state.shouldCheckAssortment = false;
        state.askForRefresh = true;
      }
      if (state.assortmentPlanInProgress == false && processing.length > 0) {
        state.assortmentPlanInProgress = true;
      }

      if (!action.payload.allocationPlanQueueUpdate) {
        state.planItems = concat(pending, queued, processing, predicting, completed, failed);
      } else {
        state.allocPlanItems = concat(pending, queued, processing, predicting, completed, failed);
      }
    },
    requestRefreshAfterPlan: (state) => {
      state.askForRefresh = true;
      state.refreshRequestedAt = Date.now();
    },
    resetPlanItem: (state) => {
      state.askForRefresh = false;
    },
    // used for both item retrieval and sending undo item
    requestUndoAction: (state) => {
      state.undoViewState = ViewDataState.liveDataLoadingNoCache;
    },
    undoUpdate: (state, _action: PayloadAction<UndoItem[]>) => {
      state.undoViewState = ViewDataState.liveDataLoadingNoCache;
    },
    receiveUndoItems: (state, action: PayloadAction<UndoItem[]>) => {
      state.undoViewState = ViewDataState.liveDataReady;
      state.undoItems = action.payload;
    },
    completeItemUndo: (state) => {
      state.undoViewState = ViewDataState.liveDataReady;
    },
    updateAddAsstTracker: (state, action: PayloadAction<AddToAssortmentTracker>) => {
      state.addToAsstInProg = action.payload.processing;
      // failures were the only request as of now, others can be added here as needed
      const userCarts = action.payload.userCarts;

      // Add the status key and flatten the userCarts data
      const flattened = flatMap(userCarts, (cartItems, status) =>
        map(cartItems, (cartItem) => ({
          ...cartItem,
          status: status as AddToAssortmentStatus,
        })),
      );

      state.addToAsstItems = flattened;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(clearAndCloseRightContainer, (state) => {
      state.undoItems = [];
    });
  },
});

export const {
  planItemUpdate,
  startPlan,
  requestRefreshAfterPlan,
  resetPlanItem,
  requestUndoAction,
  receiveUndoItems,
  completeItemUndo,
  undoUpdate,
  updateAddAsstTracker,
} = planTrackerSlice.actions;

export function fetchUndoItems() {
  return async (dispatch: AppThunkDispatch) => {
    dispatch(requestUndoAction());
    return provideAppName(async (appName) => {
      return await Axios.get<UndoItem[]>(`${UNDO_ENDPOINT}?appName=${appName}`)
        .then((resp) => {
          dispatch(receiveUndoItems(resp.data));
        })
        .catch((err: any) => {
          const msg = `An error occurred fetching Undo Item`;
          toast.error(msg);
          ServiceContainer.loggingService.error(msg, err.stack);
          Promise.reject(new Error(msg));
        });
    });
  };
}

export function asyncUndoItem(undoId: string) {
  return (dispatch: AppThunkDispatch): Promise<void> => {
    return provideAppName(async (appName) => {
      dispatch(requestUndoAction());
      return Axios.post(`${UNDO_ENDPOINT}?appName=${appName}`, {
        undo_id: undoId,
      })
        .then(() => {
          dispatch(fetchUndoItems());
        })
        .then(() => {
          toast.info('Undo successfully completed');
        })
        .catch((err: any) => {
          const msg = `An error occurred updating Undo items`;
          toast.error(msg);
          ServiceContainer.loggingService.error(msg, err.stack);
          Promise.reject(new Error(msg));
        })
        .finally(() => {
          Promise.resolve('Undo completed');
          dispatch(completeItemUndo());
        });
    });
  };
}

export default planTrackerSlice.reducer;
